import React, { useCallback } from 'react';
import {
  Routes,
  // unstable_usePrompt as usePrompt,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { FormRenderProps } from 'react-final-form';
import { Box, Tooltip } from '@mui/material';
import { common } from '@mui/material/colors';

import { ProtectedRoute } from '@occc-app/App/routes/ProtectedRoute';
import { SentryRoute as Route } from '@occc-app/App/sentry';
import {
  Page,
  Button,
  Typography,
  CircularProgress,
  ConfirmDialog,
  useAppLayoutContextConsumer,
} from '@occc-app/design-system';
import { Tab, Tabs } from '@occc/ui-design-system';
import { useGetIsUserPermittedTo, useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { SiteManagementLocationFragment } from '@occc/graphql';

import { getBreadcrumbLinks, locationUpdateRoutes } from './utils';

import { routeCreator, tabValues } from '../utils';

type LocationUpdateUpdateFormRenderComponentProps = Pick<
  FormRenderProps,
  | 'handleSubmit'
  | 'valid'
  | 'error'
  | 'dirty'
  | 'pristine'
  | 'dirtySinceLastSubmit'
  | 'modifiedSinceLastSubmit'
  | 'submitSucceeded'
> & {
  isInitiallyLoading: boolean;
  location: SiteManagementLocationFragment | null;
  disconnectMobile: () => Promise<void>;
  disconnectLoyalty: () => Promise<void>;
  canDisconnect: boolean;
  isDisconnectSiteInProgress: boolean;
};

const PAGE_TITLE = 'Location Details';

export const LocationUpdateFormRenderComponent: React.FC<
  LocationUpdateUpdateFormRenderComponentProps
> = ({
  handleSubmit,
  error,
  valid,
  isInitiallyLoading,
  pristine,
  dirtySinceLastSubmit,
  submitSucceeded,
  location,
  disconnectMobile,
  disconnectLoyalty,
  canDisconnect,
  isDisconnectSiteInProgress,
}) => {
  const navigate = useNavigate();
  const filter = useGetIsUserPermittedTo();
  const isUserPermittedToEditLocation = useIsUserPermittedTo(Feature.LOCATION_EDIT);
  const [isDisconnectMobileDialogOpen, setDisconnectMobileDialogOpen] =
    React.useState<boolean>(false);
  const [isDisconnectLoyaltyDialogOpen, setDisconnectLoyaltyDialogOpen] =
    React.useState<boolean>(false);

  // TODO: refactor later
  const form = isInitiallyLoading ? (
    <Box p={4}>
      <CircularProgress />
    </Box>
  ) : (
    <form onSubmit={handleSubmit}>
      <Routes>
        {/* TODO: routes for each tab could be used directly here for simplicity
            no need to have a separate array that we need to loop through
        */}
        {locationUpdateRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            // TODO: we could take location from the Location Context
            element={
              <ProtectedRoute feature={route.feature}>
                <route.component data={location} />
              </ProtectedRoute>
            }
          />
        ))}
      </Routes>
    </form>
  );

  let enabled = false;

  if ((!pristine && !submitSucceeded) || (dirtySinceLastSubmit && submitSucceeded)) {
    enabled = true;
  }

  if (!valid) {
    enabled = false;
  }

  const locationName = location?.siteSystemConfiguration[0]?.identifier ?? location?.uuid;

  const links = React.useMemo(() => getBreadcrumbLinks(locationName), [locationName]);
  useAppLayoutContextConsumer(links);

  const navItems = locationUpdateRoutes.filter((link) => {
    if (Array.isArray(link.feature)) {
      return link.feature.some(filter);
    }

    return filter(link.feature);
  });

  const address = location?.address;

  const locationStreet = address?.street1;
  const locationCity = address?.city;
  const locationState = address?.state;
  const locationZip = address?.zipCode;

  const headerSubtitle = address ? (
    <Typography color='primary.contrastText'>
      {locationStreet}
      <br />
      {`${locationCity}, ${locationState} ${locationZip}`}
    </Typography>
  ) : undefined;

  const { pathname } = useLocation();

  const isGeneralDetailsEdit = pathname.endsWith(`${tabValues.generalDetails}/update`);
  const isGeneralDetails = pathname.endsWith(tabValues.generalDetails);
  const isScanDataEdit = pathname.endsWith(`${tabValues.scanData}/update`);
  const isScanData = pathname.endsWith(tabValues.scanData);
  const isEditing = isScanDataEdit || isGeneralDetailsEdit;

  const tabItems = navItems
    .filter((item) => !item.skipInTabs)
    .map((navItem) => ({
      label: navItem.label,
      value: navItem.path,
    }));

  const handleTabChange = React.useCallback(
    (_event: React.ChangeEvent<{}>, value: string | number) => {
      if (typeof value === 'string') {
        navigate(value);
      }
    },
    [navigate],
  );

  const onDisconnectMobileDialogConfirm = useCallback(() => {
    disconnectMobile();
    setDisconnectMobileDialogOpen(false);
  }, [disconnectMobile]);

  const onDisconnectLoyaltyDialogConfirm = useCallback(() => {
    disconnectLoyalty();
    setDisconnectLoyaltyDialogOpen(false);
  }, [disconnectLoyalty]);

  const activeTab = React.useMemo(
    () => pathname.split('/')[3] || tabValues.generalDetails,
    [pathname],
  );

  const tabAction = {
    [tabValues.generalDetails]: (locationUuid: string, isEditing: boolean) => {
      isEditing
        ? navigate(routeCreator.generalDetails(locationUuid), { replace: true })
        : navigate(routeCreator.generalDetailsUpdate(locationUuid), { replace: true });
    },
    [tabValues.scanData]: (locationUuid: string, isEditing?: boolean) => {
      isEditing
        ? navigate(routeCreator.scanData(locationUuid), { replace: true })
        : navigate(routeCreator.scanDataUpdate(locationUuid), { replace: true });
    },
  };

  const onCancel = () => {
    if (!location?.uuid) return;
    tabAction[activeTab]?.(location.uuid, isEditing);
  };

  const onEditLocation = () => {
    if (!location?.uuid) return;
    tabAction[activeTab]?.(location.uuid, isEditing);
  };

  const renderForm = () => {
    if (error) {
      return (
        <Box p={4}>
          <Typography color='error'> Something went wrong. Please try again.</Typography>
        </Box>
      );
    }

    return form;
  };

  const getDisconnectState = () => {
    if (isInitiallyLoading) {
      return { disabled: true, reason: 'Location is still loading.' };
    }
    if (!canDisconnect) {
      return { disabled: true, reason: 'This location has no site system identifier configured.' };
    }
    if (isDisconnectSiteInProgress) {
      return { disabled: true, reason: 'Disconnect in progress…' };
    }
    return { disabled: false, reason: '' };
  };
  const { disabled: isDisconnectDisabled, reason: disconnectDisabledReason } = getDisconnectState();

  const actions = isUserPermittedToEditLocation && (
    <>
      <Tooltip title={disconnectDisabledReason || undefined}>
        <span tabIndex={disconnectDisabledReason ? 0 : undefined}>
          <Button
            color='error'
            variant='outlined'
            sx={{ background: common.white }}
            onClick={() => setDisconnectMobileDialogOpen(true)}
            disabled={isDisconnectDisabled}
          >
            Disconnect Mobile
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={disconnectDisabledReason || undefined}>
        <span tabIndex={disconnectDisabledReason ? 0 : undefined}>
          <Button
            color='error'
            variant='outlined'
            sx={{ background: common.white }}
            onClick={() => setDisconnectLoyaltyDialogOpen(true)}
            disabled={isDisconnectDisabled}
          >
            Disconnect Loyalty
          </Button>
        </span>
      </Tooltip>
      <Button
        color='secondary'
        variant='contained'
        onClick={onEditLocation}
        disabled={!(isGeneralDetails || isScanData)}
      >
        Edit Location
      </Button>
    </>
  );
  const editActions = [
    <Button key='cancel' color='secondary' variant='outlined' onClick={onCancel}>
      Cancel
    </Button>,
    <Button
      key='save'
      color='secondary'
      variant='contained'
      disabled={!enabled}
      type='submit'
      onClick={handleSubmit}
      sx={{ marginLeft: 1 }}
    >
      Save Changes
    </Button>,
  ];

  // usePrompt({ // TODO available only with React Router Data API
  //   when: enabled,
  //   message: 'You have unsaved changes. Are you sure you want to go to another page?',
  // });

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{ title: locationName || PAGE_TITLE, subtitle: headerSubtitle }}
      actions={isEditing ? editActions : actions}
    >
      <Box ml={2}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor='secondary'
          textColor='secondary'
        >
          {tabItems.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {renderForm()}

      <ConfirmDialog
        dialogTitle='Disconnect Mobile Payments Connection'
        open={isDisconnectMobileDialogOpen}
        onClose={() => setDisconnectMobileDialogOpen(false)}
        confirmButtonText='Disconnect Mobile'
        confirmButtonProps={{
          onClick: onDisconnectMobileDialogConfirm,
        }}
      >
        <Typography>
          Would you like to force this site to disconnect its Mobile connection from Open Commerce?
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        dialogTitle='Disconnect Loyalty Connection'
        open={isDisconnectLoyaltyDialogOpen}
        onClose={() => setDisconnectLoyaltyDialogOpen(false)}
        confirmButtonText='Disconnect Loyalty'
        confirmButtonProps={{
          onClick: onDisconnectLoyaltyDialogConfirm,
        }}
      >
        <Typography>
          Would you like to force this site to disconnect its Loyalty connection from Open Commerce?
        </Typography>
      </ConfirmDialog>
    </Page>
  );
};
